import { useQuery } from "@tanstack/react-query";
import { derivePhaseStatuses } from "@/lib/utils";
import type {
  Artifact,
  Criterion,
  Decision,
  Deliverable,
  Phase,
  Risk,
  TTVRow,
  WeeklyPulseRow,
  Win,
} from "@/lib/types";
import { PHASES as SEED_PHASES } from "@/data/phases";
import { DELIVERABLES as SEED_DELIVERABLES } from "@/data/deliverables";
import { RISKS as SEED_RISKS } from "@/data/risks";
import { FELIX_WINS as SEED_WINS } from "@/data/wins";
import { DECISIONS_LOG as SEED_DECISIONS } from "@/data/decisions";
import { WEEKLY_PULSE as SEED_PULSE } from "@/data/pulse";
import { TTV_DATA as SEED_TTV } from "@/data/ttv";
import { SEED_CRITERIA } from "@/data/criteria";
import { ARTIFACTS as SEED_ARTIFACTS } from "@/data/artifacts";

const REFRESH_MS = 30_000;

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return (await res.json()) as T;
}

function useLive<T>(key: string, url: string, seed: T) {
  const { data } = useQuery<T>({
    queryKey: [key],
    queryFn: () => fetchJson<T>(url),
    initialData: seed,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  return data ?? seed;
}

export function usePhases(): Phase[] {
  const raw = useLive<Phase[]>("phases", "/api/phases", SEED_PHASES);
  return derivePhaseStatuses(raw);
}
export function useDeliverables(): Deliverable[] {
  return useLive<Deliverable[]>("deliverables", "/api/deliverables", SEED_DELIVERABLES);
}
export function useRisks(): Risk[] {
  return useLive<Risk[]>("risks", "/api/risks", SEED_RISKS);
}
export function useWins(): Win[] {
  return useLive<Win[]>("wins", "/api/wins", SEED_WINS);
}
export function useDecisions(): Decision[] {
  return useLive<Decision[]>("decisions", "/api/decisions", SEED_DECISIONS);
}
export function usePulse(): WeeklyPulseRow[] {
  return useLive<WeeklyPulseRow[]>("pulse", "/api/pulse", SEED_PULSE);
}
export function useTTV(): { cohortA: TTVRow[]; cohortB: TTVRow[] } {
  return useLive<{ cohortA: TTVRow[]; cohortB: TTVRow[] }>(
    "ttv",
    "/api/ttv",
    SEED_TTV as unknown as { cohortA: TTVRow[]; cohortB: TTVRow[] }
  );
}

export function useCriteria(): Criterion[] {
  return useLive<Criterion[]>("criteria", "/api/criteria", SEED_CRITERIA);
}

export function useArtifacts(): Artifact[] {
  return useLive<Artifact[]>("artifacts", "/api/artifacts", SEED_ARTIFACTS);
}

export function useLiveStatus(): { live: boolean | null } {
  const { data } = useQuery<{ ok: boolean; live: boolean }>({
    queryKey: ["health"],
    queryFn: () => fetchJson("/api/health"),
    refetchInterval: 60_000,
  });
  return { live: data?.live ?? null };
}
